import { useEffect, useMemo, useRef, useState } from "react";
import { RequestError, useApi } from "../../services";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  TextField,
  Typography
} from "@mui/material";
import { HidroDXEditor } from "../edit";
import { ErrorOnDialog } from "./errorModal";
import { BarragemData, Mancha } from "../../context";
import { CompanyData } from "../../pages";
import { SelectCompany } from "./chooseCompany";
import { MultiPolygon, Polygon, Feature, Position } from "geojson";
import { SelectBarragem } from "./chooseDam";
import { GeometryEditorButton, getPolygon, googleSateliteHybrid, HidroLayer, HidroLayerGroup, HidroMap, PaintPolygon, StyleButton } from "../mapa";
import { PolyMancha } from "../barragem";
import { LatLngExpression, LayerGroup, Polygon as LPolygon } from "leaflet";
import { generateRandomHash, objectToHash } from "../../core";
import { LoadingModal } from "./loadingModal";

interface AddManchaProps {
  open: boolean;
  initialMancha?: PolyMancha;
  onAdd?: (mancha: PolyMancha) => void;
  onCancel?: () => void;
}

export const AddManchaModal: React.FC<AddManchaProps> = ({
  open,
  initialMancha,
  onCancel,
  onAdd,
}) => {
  const api = useApi();

  const layers = useRef(new HidroLayerGroup({ name: "Layers", showing: true, layers: [] }));

  const [error, setError] = useState<RequestError>();
  const [geomButtonKey, setGeomButtonKey] = useState('');
  const [barragem, setBarragem] = useState<BarragemData>();
  const [messageLoading, setMessageLoading] = useState<string>();
  const [style, setStyle] = useState<PaintPolygon>({
    color: "#000000ff",
    weight: 3,
    fillColor: "#00a59741",
    fillOpacity: 1
  });
  const [mancha, setMancha] = useState({
    id: initialMancha?.properties?.id ?? -1,
    idBarragem: initialMancha?.properties?.idBarragem ?? -1,
    nome: initialMancha?.properties?.nome ?? "",
    descricao: initialMancha?.properties?.descricao,
    polygon: initialMancha?.toGeoJSON() as (Feature<Polygon | MultiPolygon, { style?: PaintPolygon; }> | undefined),
  });

  // Alterando as Layers do mapa
  useEffect(() => {
    layers.current.clearLayers();
    if (mancha.polygon) {
      layers.current.push(
        new HidroLayer({
          name: "Mancha",
          showing: true,
          layer: new PolyMancha(
            invertCoords(mancha.polygon.geometry) as LatLngExpression[][],
            {
              id: mancha.id,
              idBarragem: mancha.idBarragem,
              nome: mancha.nome,
              descricao: mancha.descricao,
              dataCadastro: new Date(),
            },
            { ...style, bindPopUp: true, bindTooltip: true }
          )
        })
      );
    }

    layers.current.push(
      new HidroLayer({
        name: "Mapa",
        layer: googleSateliteHybrid(),
        showing: true,
      })
    );

    setGeomButtonKey(generateRandomHash(8));
  }, [mancha, style]);


  const handleConfirm = async () => {
    if (!barragem) {
      setError({
        error: "Erro ao adicionar Mancha de Inundação",
        message: "Você deve definir a barragem responsável pela Mancha de Inundação",
      });
      return;
    } else if (!mancha.polygon) {
      setError({
        error: "Erro ao adicionar Mancha de Inundação",
        message: "Você deve definir a geometria da Mancha de Inundação",
      });
      return;
    } else if (mancha.nome === "") {
      setError({
        error: "Erro ao adicionar Mancha de Inundação",
        message: "Você deve definir o nome da Mancha de Inundação",
      });
      return;
    }

    if (mancha.descricao === "") mancha.descricao = undefined;

    const poly = new PolyMancha(
      invertCoords(mancha.polygon.geometry) as LatLngExpression[][],
      {
        id: mancha.id,
        idBarragem: barragem.id,
        nome: mancha.nome,
        descricao: mancha.descricao,
        dataCadastro: new Date(),
      },
      { ...style, bindPopUp: true, bindTooltip: true }
    );

    const result = await api.manchas.add(poly, (loaded, total) => {
      setMessageLoading(`Adicionando mancha - ${Math.round(loaded * 100 / total)}%`);
    });

    setMessageLoading(undefined);

    if ("error" in result) {
      setError(result);
      return;
    }

    if (onAdd) onAdd(result);
  };

  const handleCancel = () => {
    if (onCancel) onCancel();
    setMancha({
      id: -1,
      idBarragem: -1,
      nome: "",
      descricao: "",
      polygon: undefined,
    });
  };

  const handleChangeGeometry = (layers: HidroLayerGroup) => {
    const layer = getPolygon(layers.layers, true);

    if (!layer) {
      setMancha({ ...mancha, polygon: undefined });
      return;
    }

    let geojson: Feature<Polygon | MultiPolygon, { style?: PaintPolygon; }>;
    if (layer.layer instanceof LayerGroup) {
      geojson = {
        type: "Feature",
        properties: { style: style },
        geometry: { type: "MultiPolygon", coordinates: layer.layer.getLayers().map(layer => (layer as LPolygon).toGeoJSON().geometry.coordinates) as Position[][][] }
      };
    } else {
      geojson = {
        type: "Feature",
        properties: { style: style },
        geometry: { type: "Polygon", coordinates: (layer.layer as LPolygon).toGeoJSON().geometry.coordinates as Position[][] }
      };
    }
    setMancha({ ...mancha, polygon: geojson });
  };

  return <>

    {/* Loading de atualização */}
    {messageLoading && <LoadingModal
      open={Boolean(messageLoading)}
      message={messageLoading}
    />}

    {error && <ErrorOnDialog
      open={Boolean(error)}
      onClose={() => setError(undefined)}
      {...error}
    />}

    <Dialog
      fullWidth
      fullScreen
      open={open}
      onClose={handleCancel}
    >
      <DialogTitle id="alert-dialog-title">Adicionar Mancha de Inundação</DialogTitle>

      <DialogContent>
        <Divider />
        <div style={{ height: "1em" }} />

        {/* Opções e Mapa */}
        <Box sx={{
          width: "100%",
          display: "flex",
          flexDirection: "row",
          gap: "1em",
          height: "70vh",
        }}>

          {/* Dados */}
          <Box sx={{
            flex: 1,
            gap: "0.5em",
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}>

            {/* Nome da Mancha */}
            <TextField
              fullWidth
              label="Nome"
              value={mancha.nome ?? ""}
              onChange={(e) => setMancha({ ...mancha, nome: e.target.value })}
            />

            {/* Barragem, Geometria e Estilo */}
            <Box sx={{ display: "flex", flexDirection: "row", gap: "0.5em", alignItems: "center" }}>

              {/* Barragem  */}
              <SelectBarragem
                initialBarragem={barragem}
                variant="contained"
                onSelect={setBarragem}
              />

              {/* Geometria */}
              <GeometryEditorButton
                key={geomButtonKey}
                variant="contained"
                startGeometry={layers.current.copy()}
                onSelect={handleChangeGeometry}
              >
                Geometria
              </GeometryEditorButton>

              {/* Estilo */}
              <StyleButton
                initialStyle={style}
                initialType="Polygon"
                onChange={setStyle}
              />

            </Box>

            {/* Descrição */}
            <Typography>Descrição:</Typography>
            <Paper elevation={1} style={{ textAlign: "justify", flex: 1, overflowY: "auto" }}>
              <HidroDXEditor
                image={false}
                chartMap={false}
                startText={mancha.descricao}
                onChange={(text) => setMancha({ ...mancha, descricao: text })}
              />
            </Paper>
          </Box>

          {/* Mapa */}
          <Box sx={{ flex: 1, height: "100%" }}>
            <HidroMap
              recenter
              animateStart={false}
              hidroLayers={layers.current}
            />
          </Box>

        </Box>
      </DialogContent>


      <DialogActions>
        <Button variant="contained" onClick={handleCancel}>
          Cancelar
        </Button>
        <Button onClick={handleConfirm} autoFocus>
          Confirmar
        </Button>
      </DialogActions>
    </Dialog >
  </>;
};



// Inverter as coordenadas do polígono
const invertCoords = (layer: Polygon | MultiPolygon) => {
  if (layer.type === "MultiPolygon") {
    return layer.coordinates.map((coords) => {
      return coords.map((coord) => {
        return coord.map(coord => [coord[1], coord[0]]);
      });
    });
  }

  return layer.coordinates.map((coords) => coords.map(coord => [coord[1], coord[0]]));
};
